import { Alert, Anchor, Badge } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { formatEuros, roundToCents } from 'src/Utils/budgetWizardMath';

import styles from './BudgetSummaryStep.module.scss';
import { Props } from './BudgetSummaryStep.types';

const BudgetSummaryStep = ({
  year,
  previousYear,
  totals,
  householdShares,
  isGovernanceBased,
  householdSharesTotal,
}: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  // An incomplete governance is shown, never silently normalised away.
  const hasIncompleteHouseholdShares =
    isGovernanceBased && Math.abs(householdSharesTotal - 100) > 0.5;
  const isSurplus = totals.balance >= 0;

  return (
    <div className={styles.container}>
      <div className={styles.equation}>
        <div className={styles.cell}>
          <div className={styles.cellLabel}>
            {t('budgetV2.summary.incomes', 'Recettes prévues')}
          </div>
          <div className={styles.cellValue}>{formatEuros(totals.incomeTotal)}</div>
        </div>
        <span className={styles.operator}>−</span>
        <div className={styles.cell}>
          <div className={styles.cellLabel}>
            {t('budgetV2.summary.expenses', 'Dépenses prévues')}
          </div>
          <div className={styles.cellValue}>{formatEuros(totals.expenseTotal)}</div>
        </div>
        <span className={styles.operator}>=</span>
        <div className={styles.cell}>
          <div className={styles.cellLabel}>
            {t('budgetV2.summary.balance', 'Solde prévisionnel')}
          </div>
          <div className={styles.cellValue}>{formatEuros(totals.balance)}</div>
          <Badge color={isSurplus ? 'green' : 'orange'} variant="light">
            {isSurplus
              ? t('budgetV2.summary.surplus', 'Excédent')
              : t('budgetV2.summary.deficit', 'Déficit')}
          </Badge>
        </div>
      </div>

      <div className={styles.capBox}>
        {t(
          'budgetV2.summary.cap',
          'Plafond annuel {{year}} : {{amount}} (calculé automatiquement — somme de vos dépenses prévues).',
          { year, amount: formatEuros(totals.calculatedCap) }
        )}
        {totals.previousYearExpenseTotal != null
          ? ` ${t(
              'budgetV2.summary.capReminder',
              'Pour mémoire, dépensé en {{year}} : {{amount}}.',
              {
                year: previousYear,
                amount: formatEuros(totals.previousYearExpenseTotal),
              }
            )}`
          : ''}
      </div>

      <div>
        <div className={styles.sectionTitle}>
          {t('budgetV2.summary.householdShares', 'Quote-part par foyer')}
        </div>
        {householdShares.length === 0 ? (
          <p className={styles.hint}>
            {t(
              'budgetV2.summary.noHousehold',
              "Aucun foyer n'est défini sur ce bien : la quote-part ne peut pas être calculée."
            )}
          </p>
        ) : (
          <>
            <p className={styles.hint}>
              {isGovernanceBased
                ? t(
                    'budgetV2.summary.governanceBased',
                    'Répartition selon les parts de la gouvernance financière, appliquée au besoin de financement.'
                  )
                : t(
                    'budgetV2.summary.equalSplit',
                    'Répartition équitable entre les foyers, appliquée au besoin de financement.'
                  )}
            </p>
            {hasIncompleteHouseholdShares ? (
              <Alert color="yellow" variant="light" mb="sm">
                {t(
                  'budgetV2.summary.householdSharesTotalWarning',
                  'Les parts des foyers totalisent {{total}} % — vérifiez la gouvernance.',
                  { total: householdSharesTotal.toLocaleString('fr-FR') }
                )}{' '}
                <Anchor component={Link} to={`/asset/${assetId}/admin?tab=membres`}>
                  {t('budgetV2.summary.openHouseholds', 'Ouvrir les foyers')}
                </Anchor>
              </Alert>
            ) : null}
            <div className={styles.shares}>
              {householdShares.map(share => {
                const annualAmount = roundToCents((totals.financingNeed * share.percentage) / 100);
                return (
                  <div key={share.householdId} className={styles.shareCard}>
                    <div className={styles.shareWho}>
                      {share.householdName} · {Math.round(share.percentage).toLocaleString('fr-FR')}{' '}
                      %
                    </div>
                    <div className={styles.shareAmount}>
                      {t('budgetV2.summary.perYear', '{{amount}} / an', {
                        amount: formatEuros(annualAmount),
                      })}
                    </div>
                    <div className={styles.shareMonthly}>
                      {t('budgetV2.summary.perMonth', 'soit {{amount}} par mois', {
                        amount: formatEuros(annualAmount / 12),
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BudgetSummaryStep;
